/* DVW Sell / Trade form.

   Asking after a piece we don't have is a different job and lives on its own page: see
   assets/dvw-request.js, which posts to the same /submit with form_type "Request a Watch".

   The submitted payload is byte-for-byte compatible with the existing Cloudflare Worker at
   /submit: the same `payload` JSON keys, the same `photo_<n>` file fields, and the same
   partial-lead JSON POST once contact details are known. Changing a key here silently breaks
   the Telegram alerts, the Klaviyo profile properties and the leads dashboard.

   Questions are data, not markup: edit the QUESTIONS array to change wording, order or options.
   An option's `v` is what the backend receives; `l` is only what the customer reads, so copy can
   be reworded without touching the backend. */
(function () {
  'use strict';

  /* The exact string the Cloudflare Worker routes on; do not reword. */
  var SELL = 'Sell or Trade a Watch';
  var STORE_KEY = 'dvw-sell-trade/v1';
  var HEIC_LIB = 'https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js';
  var KEY_LETTERS = 'ABCDEFGH';
  var MAX_FILE_BYTES = 15 * 1024 * 1024;

  var ICON_NEXT =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';
  var ICON_BACK =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 12H5M11 18l-6-6 6-6"/></svg>';
  var ICON_CHECK =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
  var ICON_UPLOAD =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 16V4M7 9l5-5 5 5"/><path d="M4 16v4h16v-4"/></svg>';

  function node(html) {
    var tpl = document.createElement('template');
    tpl.innerHTML = html.trim();
    return tpl.content.firstElementChild;
  }

  function option(raw) {
    if (raw && typeof raw === 'object') {
      return { v: raw.v, l: raw.l || raw.v, hint: raw.hint || '' };
    }
    return { v: raw, l: raw, hint: '' };
  }

  function boot(root) {
    if (!root || root.hasAttribute('data-ready')) return;
    root.setAttribute('data-ready', '1');

    var cfgEl = root.querySelector('script[data-config]');
    var CFG = {};
    try {
      CFG = JSON.parse(cfgEl ? cfgEl.textContent : '{}') || {};
    } catch (err) {
      CFG = {};
    }

    var SUBMIT_URL = CFG.submitUrl || '';
    var PREVIEW = !!CFG.designMode || root.hasAttribute('data-design-mode') || !SUBMIT_URL;
    var PHONE = CFG.phone || '';
    var WHATSAPP = CFG.whatsapp || PHONE.replace(/\D/g, '');
    var SHOP_ALL = CFG.shopAllUrl || '/collections/all';
    var HOME = CFG.homeUrl || '/';
    /* Shopify's own predictive search endpoint, the one the header search uses. */
    var SEARCH_URL = CFG.searchUrl || '/search/suggest.json';
    var CURRENCY = CFG.currency || 'USD';
    var MAX_PHOTOS = parseInt(CFG.maxPhotos, 10) > 0 ? parseInt(CFG.maxPhotos, 10) : 8;

    var stage = root.querySelector('[data-stage]');
    var progressFill = root.querySelector('[data-progress-fill]');
    if (!stage) return;

    /* -------------------------------------------------------------- questions */

    var QUESTIONS = [
      { id: 'intro', type: 'intro' },

      {
        id: 'intent',
        type: 'choice',
        required: true,
        review: 'Looking to',
        title: 'Are you looking to sell or trade?',
        options: [
          { v: 'Sell', l: 'Sell', hint: 'Cash offer' },
          { v: 'Trade', l: 'Trade', hint: 'Credit toward another piece' }
        ]
      },

      {
        id: 'location',
        type: 'choice',
        required: true,
        review: 'Location',
        title: 'Where are you located?',
        options: ['NYC / Tri-State Area', 'Elsewhere in USA', 'Outside of USA (International)']
      },

      {
        id: 'photos',
        type: 'file',
        required: true,
        review: 'Photos',
        title: 'Add photos of the watch',
        desc: 'Ideally the dial and the caseback, plus any box, papers or extras.'
      },

      {
        id: 'trade_target',
        type: 'short',
        required: true,
        suggest: true,
        when: function (a) {
          return a.intent === 'Trade';
        },
        review: 'Trading toward',
        title: "Is there a specific watch you'd like to trade for?",
        desc: "Start typing to search what's in stock, or just describe the piece you're after.",
        placeholder: 'Search our inventory, or describe the piece'
      },

      {
        id: 'contact',
        type: 'contact',
        required: true,
        review: 'Contact',
        title: 'Your contact details',
        desc: 'So a specialist can follow up with you directly.'
      },

      { id: 'review', type: 'review', title: 'Check your details' }
    ];

    function questionById(id) {
      for (var i = 0; i < QUESTIONS.length; i++) {
        if (QUESTIONS[i].id === id) return QUESTIONS[i];
      }
      return null;
    }

    /* -------------------------------------------------------------- state */

    var answers = {};
    var files = [];
    var order = [];
    var pos = 0;
    var partialSent = false;
    var submitting = false;
    var introSkipped = false;
    var historyReady = false;

    /* -------------------------------------------------------------- persistence

       Answers survive a refresh or an accidental navigation away and back. Photos cannot be
       serialised, so on restore we jump to the first question that is still incomplete rather
       than trusting a saved position that might sit past a now-empty upload step. */

    function save() {
      try {
        sessionStorage.setItem(STORE_KEY, JSON.stringify({ answers: answers, at: Date.now() }));
      } catch (err) {
        /* private browsing or full quota: persistence is a convenience, not a requirement */
      }
    }

    function clearSaved() {
      try {
        sessionStorage.removeItem(STORE_KEY);
      } catch (err) {
        /* ignore */
      }
    }

    function restore() {
      var raw;
      try {
        raw = sessionStorage.getItem(STORE_KEY);
      } catch (err) {
        return false;
      }
      if (!raw) return false;
      try {
        var data = JSON.parse(raw);
        if (!data || !data.answers) return false;
        answers = data.answers;
        /* form_type is set for every visitor, so it is not evidence that anyone got started. */
        return !!(
          answers.intent ||
          answers.watch ||
          answers.box_papers ||
          answers.location ||
          answers.trade_target ||
          answers.asking_price ||
          answers.contact
        );
      } catch (err) {
        return false;
      }
    }

    /* -------------------------------------------------------------- deep links

       ?flow=sell                      skip the intro and open on the first question
       ?flow=trade&ref=<handle>[&refs=a,b]
                                       "Trade toward this piece": intent Trade, with the listing
                                       (and anything already in the cart) pre-filled */

    function applyDeepLink() {
      var qs;
      try {
        qs = new URLSearchParams(location.search);
      } catch (err) {
        return;
      }
      var flow = (qs.get('flow') || '').toLowerCase();
      if (flow !== 'sell' && flow !== 'trade') return;

      var handleRe = /^[a-z0-9][a-z0-9-]*$/i;

      function productUrl(handle) {
        return location.origin + '/products/' + handle;
      }

      if (flow === 'trade') {
        answers.intent = 'Trade';
        var ref = qs.get('ref') || '';
        var handles = (qs.get('refs') || ref || '').split(',').filter(function (h) {
          return handleRe.test(h);
        });
        if (handles.length && !answers.trade_target) {
          answers.trade_target = handles
            .map(function (h) {
              return productUrl(h);
            })
            .join('\n');
          var q = questionById('trade_target');
          if (q) {
            q.type = 'long';
            q.title =
              handles.length > 1
                ? "The pieces you'd like to trade toward"
                : "The piece you'd like to trade toward";
            q.desc =
              "We've filled this in from what you were viewing. Edit it if anything's changed.";
          }
        }
      }

      introSkipped = true;
      /* Persist straight away: the prefill should survive a reload even if the query string is
         lost on the way (a redirect, a shared link stripped of params). */
      save();
    }

    /* -------------------------------------------------------------- flow */

    function visibleQuestions() {
      var out = [];
      for (var i = 0; i < QUESTIONS.length; i++) {
        var q = QUESTIONS[i];
        if (q.id === 'intro' && introSkipped) continue;
        if (q.when && !q.when(answers)) continue;
        out.push(i);
      }
      return out;
    }

    function numberedIndexes() {
      return order.filter(function (i) {
        var t = QUESTIONS[i].type;
        return t !== 'intro' && t !== 'review';
      });
    }

    function valueOf(q) {
      if (q.type === 'contact') return answers.contact || null;
      if (q.type === 'file') return files.length ? files : null;
      return answers[q.id] || null;
    }

    /* What the customer actually answered, as one string: the choice plus its write-in note, or
       the typed piece plus the listing link they picked. This is what the backend and the review
       screen both show, so the two can never drift apart. */
    function answerText(q) {
      if (!q) return '';
      var v = String(answers[q.id] || '').trim();
      if (!v) return '';
      if (q.writeIn && v === q.writeIn) {
        var note = String(answers[q.id + '_note'] || '').trim();
        return note ? v + ': ' + note : v;
      }
      if (q.suggest) {
        var url = String(answers[q.id + '_url'] || '').trim();
        return url ? v + '\n' + url : v;
      }
      return v;
    }

    function textFor(id) {
      return answerText(questionById(id));
    }

    /* Same answer, written for the summary screen. The attached listing is reported as a line of
       its own rather than as a raw URL: the full URL still goes to the backend, but printing it
       here cost four wrapped lines on a phone, which is what pushed the summary past the panel. */
    function reviewText(q) {
      if (q && q.suggest) {
        var v = String(answers[q.id] || '').trim();
        if (v && String(answers[q.id + '_url'] || '').trim()) return v + '\nListing attached';
        return v;
      }
      return answerText(q);
    }

    /* -------------------------------------------------------------- validation */

    function emailLooksValid(v) {
      return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v || '');
    }

    function phoneLooksValid(v) {
      return String(v || '').replace(/\D/g, '').length >= 10;
    }

    function contactErrors() {
      var c = answers.contact || {};
      var errs = {};
      if (!String(c.first || '').trim()) errs.first = 'Enter your first name.';
      if (!String(c.last || '').trim()) errs.last = 'Enter your last name.';
      if (!emailLooksValid(c.email)) errs.email = 'Enter a valid email, like name@email.com.';
      if (!phoneLooksValid(c.phone)) errs.phone = 'Enter a phone number with at least 10 digits.';
      return errs;
    }

    /* Returns true when the question passes, or a message string explaining what's missing. */
    function validate(q) {
      if (q.type === 'contact') {
        var errs = contactErrors();
        var keys = Object.keys(errs);
        return keys.length ? errs[keys[0]] : true;
      }
      if (q.type === 'file') {
        if (!q.required) return true;
        return files.length > 0 ? true : 'Add at least one photo to continue.';
      }
      if (q.type === 'choice' && q.writeIn && answers[q.id] === q.writeIn) {
        return String(answers[q.id + '_note'] || '').trim()
          ? true
          : 'Tell us what comes with it to continue.';
      }
      if (!q.required) return true;
      if (q.type === 'choice') return answers[q.id] ? true : 'Choose an option to continue.';
      if (q.type === 'short' || q.type === 'long') {
        var v = String(answers[q.id] || '').trim();
        if (!v) return 'This one is needed to continue.';
        return true;
      }
      return true;
    }

    /* After a restore we cannot trust a saved position, so recompute it. */
    function firstIncompletePos() {
      for (var i = 0; i < order.length; i++) {
        var q = QUESTIONS[order[i]];
        if (q.type === 'intro' || q.type === 'review') continue;
        if (validate(q) !== true) return i;
      }
      return Math.max(0, order.length - 1);
    }

    /* -------------------------------------------------------------- rendering */

    function activeScreen() {
      return stage.querySelector('[data-active]');
    }

    function swap(screen) {
      var old = activeScreen();
      if (old) {
        old.removeAttribute('data-active');
        old.setAttribute('data-leaving', '');
        setTimeout(function () {
          if (old.parentNode) old.parentNode.removeChild(old);
        }, 400);
      }
      stage.appendChild(screen);
      /* Forcing a reflow commits the pre-transition style, so the fade-in plays without
         requestAnimationFrame. rAF is throttled in background tabs, which would otherwise leave
         the form invisible until the tab is focused. */
      void screen.offsetHeight;
      screen.setAttribute('data-active', '');
    }

    function syncChrome(q, index, total) {
      if (progressFill) {
        var pct = 2;
        if (q.type === 'review') pct = 100;
        else if (q.type !== 'intro' && total > 0) pct = Math.max(6, Math.round((index / total) * 100));
        progressFill.style.width = pct + '%';
      }

      if (q.type === 'intro') root.removeAttribute('data-started');
      else root.setAttribute('data-started', '');
    }

    function flowLabel() {
      return answers.intent === 'Trade' ? 'Trade a watch' : 'Sell or trade';
    }

    function buildChoices(opts, selected, labelledBy) {
      var wrap = node(
        '<div class="dvw-st__choices" role="radiogroup" aria-labelledby="' + labelledBy + '"></div>'
      );

      opts.forEach(function (raw, i) {
        var o = option(raw);
        var btn = node(
          '<button type="button" class="dvw-st__choice" role="radio" aria-checked="false">' +
            '<span class="dvw-st__key"></span>' +
            '<span class="dvw-st__choicetext"></span>' +
            '<span class="dvw-st__tick">' +
            ICON_CHECK +
            '</span>' +
            '</button>'
        );
        btn.setAttribute('data-value', o.v);
        btn.querySelector('.dvw-st__key').textContent = KEY_LETTERS[i] || String(i + 1);

        var text = btn.querySelector('.dvw-st__choicetext');
        text.textContent = o.l;
        if (o.hint) {
          var hint = document.createElement('span');
          hint.className = 'dvw-st__dropsub';
          hint.textContent = o.hint;
          text.appendChild(hint);
        }

        if (selected === o.v) {
          btn.setAttribute('aria-checked', 'true');
          btn.setAttribute('tabindex', '0');
        } else {
          btn.setAttribute('tabindex', selected ? '-1' : i === 0 ? '0' : '-1');
        }
        wrap.appendChild(btn);
      });

      return wrap;
    }

    function buildField(q, titleId) {
      if (q.type === 'short') {
        var input = node('<input type="text" class="dvw-st__input" autocomplete="off">');
        input.setAttribute('aria-labelledby', titleId);
        input.setAttribute('data-field', '');
        input.placeholder = q.placeholder || '';
        input.value = answers[q.id] || '';
        if (q.id === 'asking_price') input.inputMode = 'text';
        if (!q.suggest) return input;

        var combo = node('<div class="dvw-st__combo"></div>');
        input.setAttribute('role', 'combobox');
        input.setAttribute('aria-expanded', 'false');
        input.setAttribute('aria-autocomplete', 'list');
        input.setAttribute('aria-controls', 'dvw-st-suggest');
        combo.appendChild(input);
        var list = node(
          '<ul class="dvw-st__suggest" id="dvw-st-suggest" role="listbox" data-suggest hidden></ul>'
        );
        combo.appendChild(list);
        var picked = node(
          '<p class="dvw-st__picked" data-picked hidden>' +
            '<span class="dvw-st__pickedlabel">Listing attached</span>' +
            '<span class="dvw-st__pickedtext"></span>' +
            '<button type="button" class="dvw-st__btn dvw-st__btn--link" data-picked-clear>Clear</button>' +
            '</p>'
        );
        combo.appendChild(picked);
        return combo;
      }

      if (q.type === 'long') {
        var ta = node('<textarea class="dvw-st__textarea" rows="4"></textarea>');
        ta.setAttribute('aria-labelledby', titleId);
        ta.setAttribute('data-field', '');
        ta.placeholder = q.placeholder || '';
        ta.value = answers[q.id] || '';
        return ta;
      }

      if (q.type === 'choice') {
        var choices = buildChoices(q.options, answers[q.id], titleId);
        if (!q.writeIn) return choices;

        var frag = document.createDocumentFragment();
        frag.appendChild(choices);
        var wrap = node('<div class="dvw-st__writein" data-writein-wrap hidden></div>');
        var label = node('<label class="dvw-st__label" for="dvw-st-writein"></label>');
        label.textContent = q.writeLabel || 'Please describe';
        var write = node('<input type="text" class="dvw-st__input" id="dvw-st-writein" autocomplete="off" data-writein>');
        write.placeholder = q.writePlaceholder || '';
        write.value = answers[q.id + '_note'] || '';
        wrap.appendChild(label);
        wrap.appendChild(write);
        if (answers[q.id] === q.writeIn) wrap.removeAttribute('hidden');
        frag.appendChild(wrap);
        return frag;
      }

      if (q.type === 'contact') {
        var c = answers.contact || {};
        var grid = node('<div class="dvw-st__grid"></div>');

        function field(id, label, type, extra, full) {
          var wrap = node('<div class="dvw-st__field"></div>');
          if (full) wrap.classList.add('dvw-st__field--full');
          var lab = node('<label class="dvw-st__label"></label>');
          lab.setAttribute('for', id);
          lab.textContent = label;
          var inp = node('<input class="dvw-st__input">');
          inp.id = id;
          inp.type = type;
          inp.setAttribute('data-contact', extra.key);
          inp.setAttribute('autocomplete', extra.autocomplete);
          if (extra.inputMode) inp.inputMode = extra.inputMode;
          if (extra.placeholder) inp.placeholder = extra.placeholder;
          inp.value = c[extra.key] || '';
          var err = node('<p class="dvw-st__fielderr"></p>');
          err.id = id + '-error';
          inp.setAttribute('aria-describedby', err.id);
          wrap.appendChild(lab);
          wrap.appendChild(inp);
          wrap.appendChild(err);
          return wrap;
        }

        grid.appendChild(
          field('dvw-st-first', 'First name', 'text', { key: 'first', autocomplete: 'given-name' })
        );
        grid.appendChild(
          field('dvw-st-last', 'Last name', 'text', { key: 'last', autocomplete: 'family-name' })
        );
        grid.appendChild(
          field(
            'dvw-st-email',
            'Email',
            'email',
            { key: 'email', autocomplete: 'email', inputMode: 'email' },
            true
          )
        );
        grid.appendChild(
          field(
            'dvw-st-phone',
            'Phone number',
            'tel',
            {
              key: 'phone',
              autocomplete: 'tel',
              inputMode: 'tel',
              placeholder: '(555) 123-4567'
            },
            true
          )
        );

        var consentWrap = node('<div class="dvw-st__field dvw-st__field--full"></div>');
        var consentLabel = node(
          '<label class="dvw-st__consent">' +
            '<input type="checkbox" class="dvw-st__checkbox" id="dvw-st-consent">' +
            '<span></span>' +
            '</label>'
        );
        consentLabel.querySelector('span').textContent =
          "Email me about pieces that match what I'm after, new arrivals and market updates. Unsubscribe anytime.";
        consentLabel.querySelector('input').checked = !!c.consent;
        consentWrap.appendChild(consentLabel);
        grid.appendChild(consentWrap);

        return grid;
      }

      if (q.type === 'file') {
        var frag2 = document.createDocumentFragment();
        var drop = node(
          '<button type="button" class="dvw-st__drop" data-drop>' +
            ICON_UPLOAD +
            '<span class="dvw-st__dropmain">Tap to upload or drag photos here</span>' +
            '<span class="dvw-st__dropsub"></span>' +
            '</button>'
        );
        drop.querySelector('.dvw-st__dropsub').textContent =
          'JPG, PNG or HEIC · up to ' + MAX_PHOTOS + ' photos';
        var input2 = node(
          '<input type="file" accept="image/*,.heic,.heif" multiple hidden data-file-input>'
        );
        var thumbs = node('<ul class="dvw-st__thumbs" data-thumbs></ul>');
        frag2.appendChild(drop);
        frag2.appendChild(input2);
        frag2.appendChild(thumbs);
        return frag2;
      }

      return document.createDocumentFragment();
    }

    /* -------------------------------------------------------------- review step */

    function reviewRows() {
      var rows = [];
      order.forEach(function (qi) {
        var q = QUESTIONS[qi];
        if (q.type === 'intro' || q.type === 'review') return;

        if (q.type === 'contact') {
          var c = answers.contact || {};
          var lines = [
            (String(c.first || '') + ' ' + String(c.last || '')).trim(),
            c.email || '',
            c.phone || ''
          ].filter(Boolean);
          rows.push({ label: q.review || 'Contact', value: lines.join('\n'), target: q.id });
          return;
        }

        if (q.type === 'file') {
          rows.push({
            label: q.review || 'Photos',
            value: files.length
              ? files.length + (files.length === 1 ? ' photo attached' : ' photos attached')
              : '',
            target: q.id
          });
          return;
        }

        rows.push({ label: q.review || q.title, value: reviewText(q), target: q.id });
      });
      return rows;
    }

    function buildReview() {
      var wrap = node('<div class="dvw-st__review"></div>');
      reviewRows().forEach(function (row) {
        var el = node(
          '<div class="dvw-st__reviewrow">' +
            '<p class="dvw-st__reviewkey"></p>' +
            '<p class="dvw-st__reviewval"></p>' +
            '<button type="button" class="dvw-st__btn dvw-st__btn--link">Edit</button>' +
            '</div>'
        );
        el.querySelector('.dvw-st__reviewkey').textContent = row.label;
        var val = el.querySelector('.dvw-st__reviewval');
        if (row.value) {
          val.textContent = row.value;
        } else {
          val.textContent = 'Not provided';
          val.setAttribute('data-empty', '');
        }
        el.querySelector('button').addEventListener('click', function () {
          var idx = order.indexOf(QUESTIONS.indexOf(questionById(row.target)));
          if (idx > -1) {
            pos = idx;
            pushHistory();
            render();
          }
        });
        wrap.appendChild(el);
      });
      return wrap;
    }

    /* -------------------------------------------------------------- screens */

    function render() {
      order = visibleQuestions();
      if (pos >= order.length) pos = order.length - 1;
      if (pos < 0) pos = 0;

      var qi = order[pos];
      var q = QUESTIONS[qi];
      var nums = numberedIndexes();
      var index = nums.indexOf(qi) + 1;
      var titleId = 'dvw-st-title';

      syncChrome(q, index, nums.length);

      var screen;
      if (q.type === 'intro') screen = renderIntro(titleId);
      else if (q.type === 'review') screen = renderReview(titleId);
      else screen = renderQuestion(q, qi, titleId, index, nums.length);

      swap(screen);
      focusFirst(screen, q);
      scrollIntoPlace();
    }

    function renderIntro(titleId) {
      var screen = node('<div class="dvw-st__screen"></div>');

      var title = node('<h1 class="dvw-st__title dvw-st__title--lg"></h1>');
      title.id = titleId;
      title.textContent = CFG.introTitle || 'Sell or Trade Your Watch';
      screen.appendChild(title);

      if (CFG.introText) {
        var desc = node('<p class="dvw-st__desc"></p>');
        desc.textContent = CFG.introText;
        screen.appendChild(desc);
      }

      var actions = node('<div class="dvw-st__actions"></div>');
      /* data-next marks the primary action on every screen; the keyboard handler uses its
         presence to tell an interactive screen from a loading / thank-you one. */
      var start = node(
        '<button type="button" class="dvw-st__btn" data-next>Get started ' + ICON_NEXT + '</button>'
      );
      start.addEventListener('click', function () {
        goTo(pos + 1);
      });
      actions.appendChild(start);
      var hint = node('<span class="dvw-st__hint">or press <kbd>Enter</kbd></span>');
      actions.appendChild(hint);
      screen.appendChild(actions);

      return screen;
    }

    function renderQuestion(q, qi, titleId, index, total) {
      var screen = node('<div class="dvw-st__screen"></div>');

      var meta = node(
        '<p class="dvw-st__flow"><span data-flow></span><span class="dvw-st__count" data-count></span></p>'
      );
      meta.querySelector('[data-flow]').textContent = flowLabel();
      meta.querySelector('[data-count]').textContent =
        total > 0 ? 'Step ' + index + ' of ' + total : '';
      screen.appendChild(meta);

      var title = node('<h2 class="dvw-st__title"></h2>');
      title.id = titleId;
      title.textContent = q.title;
      if (q.required) {
        var req = node('<span class="dvw-st__req" aria-hidden="true"> *</span>');
        title.appendChild(req);
      }
      screen.appendChild(title);

      if (q.desc) {
        /* Authored copy from this file only, never customer input, so markup is intentional. */
        var desc = node('<p class="dvw-st__desc"></p>');
        desc.innerHTML = q.desc;
        screen.appendChild(desc);
      }

      screen.appendChild(buildField(q, titleId));

      var err = node('<p class="dvw-st__error" role="alert" data-error></p>');
      screen.appendChild(err);

      var actions = node('<div class="dvw-st__actions"></div>');
      if (pos > 0) {
        var back = node(
          '<button type="button" class="dvw-st__btn dvw-st__btn--ghost">' + ICON_BACK + 'Back</button>'
        );
        back.addEventListener('click', function () {
          collect(q, screen);
          save();
          goTo(pos - 1);
        });
        actions.appendChild(back);
      }
      actions.appendChild(node('<span class="dvw-st__spacer"></span>'));

      if (!q.required) {
        var skip = node('<button type="button" class="dvw-st__btn dvw-st__btn--link">Skip</button>');
        skip.addEventListener('click', function () {
          collect(q, screen);
          save();
          goTo(pos + 1);
        });
        actions.appendChild(skip);
      }

      var isLast = pos === order.length - 1;
      var next = node(
        '<button type="button" class="dvw-st__btn" data-next>' +
          (isLast ? 'Submit' : 'Continue') +
          ' ' +
          ICON_NEXT +
          '</button>'
      );
      next.addEventListener('click', function () {
        advance(q, screen);
      });
      actions.appendChild(next);
      screen.appendChild(actions);

      wireQuestion(q, screen);
      return screen;
    }

    function renderReview(titleId) {
      var screen = node('<div class="dvw-st__screen dvw-st__screen--review"></div>');

      var title = node('<h2 class="dvw-st__title"></h2>');
      title.id = titleId;
      title.textContent = 'Check your details';
      screen.appendChild(title);

      var desc = node('<p class="dvw-st__desc"></p>');
      desc.textContent = 'Edit anything that needs a change, then send it over.';
      screen.appendChild(desc);

      screen.appendChild(buildReview());

      var err = node('<p class="dvw-st__error" role="alert" data-error></p>');
      screen.appendChild(err);

      var actions = node('<div class="dvw-st__actions"></div>');
      var back = node(
        '<button type="button" class="dvw-st__btn dvw-st__btn--ghost">' + ICON_BACK + 'Back</button>'
      );
      back.addEventListener('click', function () {
        goTo(pos - 1);
      });
      actions.appendChild(back);
      actions.appendChild(node('<span class="dvw-st__spacer"></span>'));

      var send = node(
        '<button type="button" class="dvw-st__btn" data-next>Send my details ' + ICON_NEXT + '</button>'
      );
      send.addEventListener('click', function () {
        submit();
      });
      actions.appendChild(send);
      screen.appendChild(actions);

      return screen;
    }

    function focusFirst(screen, q) {
      if (pos === 0 && q.type === 'intro') return;
      var target =
        screen.querySelector('[data-field]') ||
        screen.querySelector('[data-contact]') ||
        screen.querySelector('[aria-checked="true"]') ||
        screen.querySelector('.dvw-st__choice') ||
        screen.querySelector('[data-drop]');
      if (!target) return;
      setTimeout(function () {
        try {
          target.focus({ preventScroll: true });
        } catch (err) {
          /* older Safari ignores the options bag */
        }
      }, 380);
    }

    /* The panel is sized to fill the screen below whatever the theme puts above it. The
       stylesheet's fallback adds up the announcement bar and the header, which is right to within
       a pixel or two, but a header border, a promo bar or a different page template can all throw
       that off, and the whole point of a fixed panel is that it lands inside the viewport. So
       measure it instead. Scroll position is irrelevant here: this is a document offset. */
    function fitToScreen() {
      var top = root.getBoundingClientRect().top + (window.scrollY || 0);
      root.style.setProperty('--dvw-st-chrome', Math.max(0, Math.round(top)) + 'px');
    }

    function scrollIntoPlace() {
      var rect = root.getBoundingClientRect();
      if (rect.top >= 0) return;
      var styles = getComputedStyle(document.documentElement);
      var offset = parseInt(styles.getPropertyValue('--header-height'), 10) || 0;
      window.scrollTo({ top: window.scrollY + rect.top - offset, behavior: 'smooth' });
    }

    /* -------------------------------------------------------------- wiring */

    function wireQuestion(q, screen) {
      if (q.type === 'choice') {
        var writeWrap = screen.querySelector('[data-writein-wrap]');
        var writeInput = screen.querySelector('[data-writein]');
        if (writeInput) {
          writeInput.addEventListener('input', function () {
            answers[q.id + '_note'] = writeInput.value;
            hideError(screen);
          });
          writeInput.addEventListener('blur', function () {
            collect(q, screen);
            save();
          });
        }

        wireChoiceGroup(screen.querySelector('.dvw-st__choices'), function (v) {
          answers[q.id] = v;
          save();

          /* "Other" needs a written answer, so it opens a line instead of moving on. */
          if (q.writeIn && v === q.writeIn) {
            if (writeWrap) writeWrap.removeAttribute('hidden');
            hideError(screen);
            if (writeInput) {
              try {
                writeInput.focus({ preventScroll: true });
              } catch (err) {
                writeInput.focus();
              }
            }
            return;
          }
          if (writeWrap) writeWrap.setAttribute('hidden', '');

          /* Picking a branch answer changes which questions follow, so settle then advance. */
          setTimeout(function () {
            advance(q, screen);
          }, 200);
        });
      }

      if (q.type === 'contact') {
        screen.querySelectorAll('[data-contact]').forEach(function (input) {
          input.addEventListener('blur', function () {
            collect(q, screen);
            save();
            showFieldErrors(screen, true);
          });
          input.addEventListener('input', function () {
            var field = input.closest('.dvw-st__field');
            if (field && field.hasAttribute('data-invalid')) {
              field.removeAttribute('data-invalid');
              input.setAttribute('aria-invalid', 'false');
            }
            hideError(screen);
          });
        });
        var consent = screen.querySelector('#dvw-st-consent');
        if (consent) {
          consent.addEventListener('change', function () {
            collect(q, screen);
            save();
          });
        }
      }

      if (q.type === 'short' || q.type === 'long') {
        var field = screen.querySelector('[data-field]');
        if (field) {
          field.addEventListener('input', function () {
            answers[q.id] = field.value;
            hideError(screen);
          });
          field.addEventListener('blur', save);
        }
        if (q.suggest && q.type === 'short') wireSuggest(q, screen);
      }

      if (q.type === 'file') wireUpload(q, screen);
    }

    /* -------------------------------------------------------------- inventory suggestions

       Uses Shopify's own predictive search endpoint, the same one the header search box calls,
       limited to products that are actually available. Picking a result attaches that listing's
       URL to the answer, so the team sees exactly which piece the customer means. Typing
       anything else still works: the search is a shortcut, not a requirement. */

    var suggestSeq = 0;

    function money(amount) {
      var n = parseFloat(amount);
      if (!isFinite(n)) return '';
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: CURRENCY,
          maximumFractionDigits: 0
        }).format(n);
      } catch (err) {
        return '$' + Math.round(n).toLocaleString();
      }
    }

    function absoluteUrl(path) {
      try {
        return new URL(path, location.href).href;
      } catch (err) {
        return path;
      }
    }

    function suggestUrl(term) {
      return (
        SEARCH_URL +
        (SEARCH_URL.indexOf('?') > -1 ? '&' : '?') +
        'q=' +
        encodeURIComponent(term) +
        '&resources[type]=product' +
        '&resources[limit]=6' +
        '&resources[options][unavailable_products]=hide' +
        '&resources[options][fields]=title,vendor,product_type,variants.sku,variants.title'
      );
    }

    function wireSuggest(q, screen) {
      var input = screen.querySelector('[data-field]');
      var list = screen.querySelector('[data-suggest]');
      var picked = screen.querySelector('[data-picked]');
      if (!input || !list) return;

      var items = [];
      var active = -1;
      var timer = null;

      function showPicked() {
        if (!picked) return;
        var url = answers[q.id + '_url'];
        if (url) {
          picked.querySelector('.dvw-st__pickedtext').textContent = url;
          picked.removeAttribute('hidden');
        } else {
          picked.setAttribute('hidden', '');
        }
      }

      function close() {
        list.textContent = '';
        list.setAttribute('hidden', '');
        input.setAttribute('aria-expanded', 'false');
        items = [];
        active = -1;
      }

      function highlight(next) {
        if (!items.length) return;
        active = (next + items.length) % items.length;
        items.forEach(function (el, i) {
          if (i === active) {
            el.setAttribute('data-active', '');
            el.setAttribute('aria-selected', 'true');
          } else {
            el.removeAttribute('data-active');
            el.setAttribute('aria-selected', 'false');
          }
        });
      }

      function choose(product) {
        answers[q.id] = product.title;
        answers[q.id + '_url'] = absoluteUrl('/products/' + product.handle);
        input.value = product.title;
        save();
        close();
        showPicked();
        hideError(screen);
      }

      function paint(products) {
        list.textContent = '';
        items = [];
        active = -1;

        if (!products.length) {
          close();
          return;
        }

        products.forEach(function (p) {
          var li = node('<li class="dvw-st__suggestitem" role="option" aria-selected="false"></li>');
          var btn = node('<button type="button" class="dvw-st__suggestbtn"></button>');
          if (p.image) {
            var img = node('<img class="dvw-st__suggestimg" alt="" loading="lazy">');
            img.src = p.image;
            btn.appendChild(img);
          }
          var text = node('<span class="dvw-st__suggesttext"></span>');
          var name = node('<span class="dvw-st__suggestname"></span>');
          name.textContent = p.title;
          text.appendChild(name);
          if (p.price) {
            var price = node('<span class="dvw-st__suggestprice"></span>');
            price.textContent = p.price;
            text.appendChild(price);
          }
          btn.appendChild(text);
          /* mousedown, not click: the input's blur would otherwise tear the list down first. */
          btn.addEventListener('mousedown', function (event) {
            event.preventDefault();
            choose(p);
          });
          btn.addEventListener('click', function () {
            choose(p);
          });
          li.appendChild(btn);
          list.appendChild(li);
          items.push(li);
        });

        list.removeAttribute('hidden');
        input.setAttribute('aria-expanded', 'true');
      }

      function search(term) {
        var seq = ++suggestSeq;
        fetch(suggestUrl(term), { headers: { Accept: 'application/json' } })
          .then(function (res) {
            return res.json();
          })
          .then(function (data) {
            /* Ignore answers to questions we have stopped asking: a slower earlier request, or a
               response that lands after the customer has moved on. */
            if (seq !== suggestSeq || !input.isConnected) return;
            var found =
              (data &&
                data.resources &&
                data.resources.results &&
                data.resources.results.products) ||
              [];
            paint(
              found
                .filter(function (p) {
                  return p && p.handle;
                })
                .map(function (p) {
                  return {
                    title: p.title || '',
                    handle: p.handle,
                    price: money(p.price),
                    image: p.featured_image && p.featured_image.url ? p.featured_image.url : ''
                  };
                })
            );
          })
          .catch(function () {
            /* The search is a convenience; typing by hand still submits. */
            if (seq === suggestSeq) close();
          });
      }

      input.addEventListener('input', function () {
        /* Editing the text means the attached listing no longer matches what they typed. */
        if (answers[q.id + '_url']) {
          delete answers[q.id + '_url'];
          showPicked();
        }
        var term = String(input.value || '').trim();
        if (timer) clearTimeout(timer);
        if (term.length < 2) {
          suggestSeq++;
          close();
          return;
        }
        timer = setTimeout(function () {
          search(term);
        }, 220);
      });

      input.addEventListener('keydown', function (event) {
        if (!items.length) return;
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          highlight(active + (event.key === 'ArrowDown' ? 1 : -1));
          return;
        }
        if (event.key === 'Escape') {
          close();
          return;
        }
        if (event.key === 'Enter' && active > -1) {
          /* Take the highlighted result instead of letting Enter move to the next question. */
          event.preventDefault();
          event.stopPropagation();
          items[active].querySelector('button').click();
        }
      });

      input.addEventListener('blur', function () {
        setTimeout(close, 120);
      });

      if (picked) {
        picked.querySelector('[data-picked-clear]').addEventListener('click', function () {
          delete answers[q.id + '_url'];
          save();
          showPicked();
          input.focus();
        });
      }

      showPicked();
    }

    function wireChoiceGroup(group, onPick) {
      if (!group) return;
      var choices = Array.prototype.slice.call(group.querySelectorAll('.dvw-st__choice'));

      function select(el) {
        choices.forEach(function (c) {
          c.setAttribute('aria-checked', 'false');
          c.setAttribute('tabindex', '-1');
        });
        el.setAttribute('aria-checked', 'true');
        el.setAttribute('tabindex', '0');
        onPick(el.getAttribute('data-value'));
      }

      choices.forEach(function (el, i) {
        el.addEventListener('click', function () {
          select(el);
        });
        el.addEventListener('keydown', function (event) {
          var key = event.key;
          if (key === ' ' || key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            select(el);
            return;
          }
          var step = key === 'ArrowDown' || key === 'ArrowRight' ? 1 : key === 'ArrowUp' || key === 'ArrowLeft' ? -1 : 0;
          if (!step) return;
          event.preventDefault();
          var nextEl = choices[(i + step + choices.length) % choices.length];
          nextEl.setAttribute('tabindex', '0');
          el.setAttribute('tabindex', '-1');
          nextEl.focus();
        });
      });
    }

    /* -------------------------------------------------------------- uploads */

    var heicLoader = null;

    function loadHeic() {
      if (window.heic2any) return Promise.resolve();
      if (heicLoader) return heicLoader;
      heicLoader = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = HEIC_LIB;
        s.async = true;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });
      return heicLoader;
    }

    function isHeic(file) {
      return /\.hei[cf]$/i.test(file.name || '') || file.type === 'image/heic' || file.type === 'image/heif';
    }

    function fileKey(file) {
      return [file.name, file.size, file.lastModified].join('|');
    }

    function wireUpload(q, screen) {
      var drop = screen.querySelector('[data-drop]');
      var input = screen.querySelector('[data-file-input]');
      if (!drop || !input) return;

      drop.addEventListener('click', function () {
        input.click();
      });
      input.addEventListener('change', function () {
        addFiles(input.files, screen);
        input.value = '';
      });
      ['dragenter', 'dragover'].forEach(function (ev) {
        drop.addEventListener(ev, function (event) {
          event.preventDefault();
          drop.setAttribute('data-dragging', '');
        });
      });
      ['dragleave', 'drop'].forEach(function (ev) {
        drop.addEventListener(ev, function (event) {
          event.preventDefault();
          drop.removeAttribute('data-dragging');
        });
      });
      drop.addEventListener('drop', function (event) {
        if (event.dataTransfer) addFiles(event.dataTransfer.files, screen);
      });

      renderThumbs(screen);
    }

    function addFiles(list, screen) {
      var incoming = Array.prototype.slice.call(list || []);
      var pending = [];

      incoming.forEach(function (file) {
        if (files.length + pending.length >= MAX_PHOTOS) return;
        if (!/^image\//.test(file.type || '') && !isHeic(file)) return;
        if (file.size > MAX_FILE_BYTES) {
          showError(screen, file.name + ' is over 15MB, so it was skipped.');
          return;
        }
        var key = fileKey(file);
        var already = files.some(function (f) {
          return f.key === key;
        });
        if (already) return;

        var entry = { key: key, file: file, url: '', converting: isHeic(file) };
        try {
          entry.url = URL.createObjectURL(file);
        } catch (err) {
          entry.url = '';
        }
        files.push(entry);
        pending.push(entry);
      });

      if (incoming.length && files.length >= MAX_PHOTOS) {
        showError(screen, 'That is the maximum of ' + MAX_PHOTOS + ' photos.');
      }

      renderThumbs(screen);
      hideError(screen);

      /* HEIC from iPhones is converted in the browser so the team receives viewable JPEGs. */
      var heics = pending.filter(function (e) {
        return e.converting;
      });
      if (!heics.length) return;

      loadHeic()
        .then(function () {
          return Promise.all(
            heics.map(function (entry) {
              return window
                .heic2any({ blob: entry.file, toType: 'image/jpeg', quality: 0.85 })
                .then(function (jpg) {
                  var name = (entry.file.name || 'photo').replace(/\.(heic|heif)$/i, '.jpg');
                  entry.file = new File([jpg], name, { type: 'image/jpeg' });
                  if (entry.url) URL.revokeObjectURL(entry.url);
                  entry.url = URL.createObjectURL(entry.file);
                })
                .catch(function () {
                  /* keep the original: the Worker stores it and the dashboard converts on read */
                })
                .then(function () {
                  entry.converting = false;
                });
            })
          );
        })
        .catch(function () {
          heics.forEach(function (entry) {
            entry.converting = false;
          });
        })
        .then(function () {
          renderThumbs(screen);
        });
    }

    function renderThumbs(screen) {
      var wrap = screen.querySelector('[data-thumbs]');
      if (!wrap) return;
      wrap.textContent = '';

      files.forEach(function (entry) {
        var li = node(
          '<li class="dvw-st__thumb">' +
            '<img alt="">' +
            '<button type="button" class="dvw-st__thumbdrop" aria-label="Remove photo">&times;</button>' +
            '</li>'
        );
        var img = li.querySelector('img');
        if (entry.url) img.src = entry.url;
        if (entry.converting) {
          li.appendChild(node('<span class="dvw-st__thumbbusy">Converting</span>'));
        }
        li.querySelector('button').addEventListener('click', function () {
          var i = files.indexOf(entry);
          if (i > -1) {
            if (entry.url) URL.revokeObjectURL(entry.url);
            files.splice(i, 1);
          }
          renderThumbs(screen);
        });
        wrap.appendChild(li);
      });
    }

    /* -------------------------------------------------------------- collect + errors */

    function collect(q, screen) {
      screen = screen || activeScreen();
      if (!screen) return;

      if (q.type === 'short' || q.type === 'long') {
        var field = screen.querySelector('[data-field]');
        if (field) answers[q.id] = String(field.value || '').trim();
        return;
      }

      if (q.type === 'choice' && q.writeIn) {
        var write = screen.querySelector('[data-writein]');
        if (write) answers[q.id + '_note'] = String(write.value || '').trim();
        return;
      }

      if (q.type === 'contact') {
        var c = answers.contact || {};
        screen.querySelectorAll('[data-contact]').forEach(function (input) {
          c[input.getAttribute('data-contact')] = String(input.value || '').trim();
        });
        var consent = screen.querySelector('#dvw-st-consent');
        c.consent = consent ? !!consent.checked : !!c.consent;
        answers.contact = c;
        answers.consent = c.consent;
      }
    }

    function showFieldErrors(screen, onlyTouched) {
      var errs = contactErrors();
      screen.querySelectorAll('[data-contact]').forEach(function (input) {
        var key = input.getAttribute('data-contact');
        var field = input.closest('.dvw-st__field');
        var errEl = field ? field.querySelector('.dvw-st__fielderr') : null;
        var hasValue = String(input.value || '').trim().length > 0;
        if (errs[key] && (!onlyTouched || hasValue || document.activeElement !== input)) {
          if (field) field.setAttribute('data-invalid', '');
          if (errEl) errEl.textContent = errs[key];
          input.setAttribute('aria-invalid', 'true');
        } else {
          if (field) field.removeAttribute('data-invalid');
          input.setAttribute('aria-invalid', 'false');
        }
      });
      return Object.keys(errs).length === 0;
    }

    function showError(screen, message) {
      var el = (screen || activeScreen() || root).querySelector('[data-error]');
      if (!el) return;
      el.textContent = message;
      el.setAttribute('data-shown', '');
    }

    function hideError(screen) {
      var el = (screen || activeScreen() || root).querySelector('[data-error]');
      if (!el) return;
      el.textContent = '';
      el.removeAttribute('data-shown');
    }

    /* -------------------------------------------------------------- navigation */

    function pushHistory() {
      if (!historyReady) return;
      try {
        history.pushState({ dvwPos: pos }, '');
      } catch (err) {
        /* history is a nicety; navigation still works without it */
      }
    }

    function goTo(next) {
      pos = Math.max(0, Math.min(next, order.length - 1));
      pushHistory();
      render();
    }

    function advance(q, screen) {
      collect(q, screen);
      save();

      if (q.type === 'contact' && !showFieldErrors(screen, false)) {
        showError(screen, 'Check the highlighted fields to continue.');
        return;
      }

      var ok = validate(q);
      if (ok !== true) {
        showError(screen, ok);
        return;
      }

      if (q.id === 'contact') sendPartialLead();

      order = visibleQuestions();
      if (pos >= order.length - 1) {
        submit();
        return;
      }
      goTo(pos + 1);
    }

    /* -------------------------------------------------------------- submit */

    /* Only what this form actually asks. The backend keys off form_type and needs the contact
       block, so those always ship; every answer key is sent only once it has a value, which keeps
       retired questions out of the Telegram alerts and the Klaviyo profile entirely. */
    var ANSWER_KEYS = ['intent', 'watch', 'box_papers', 'asking_price', 'location', 'trade_target'];

    function payload(partial) {
      var out = {
        partial: partial,
        form_type: answers.form_type || '',
        consent: !!answers.consent,
        contact: answers.contact || {},
        submitted_at: new Date().toISOString(),
        page: location.href
      };
      ANSWER_KEYS.forEach(function (id) {
        var value = questionById(id) ? answerText(questionById(id)) : String(answers[id] || '').trim();
        if (value) out[id] = value;
      });
      return out;
    }

    /* Fires once contact details are known so the team can follow up on abandoned forms. */
    function sendPartialLead() {
      if (partialSent || PREVIEW) return;
      var c = answers.contact || {};
      if (!c.email || !c.first) return;
      partialSent = true;
      try {
        fetch(SUBMIT_URL, {
          method: 'POST',
          mode: 'cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload(true)),
          keepalive: true
        }).catch(function () {});
      } catch (err) {
        /* never block the form on the partial capture */
      }
    }

    function submit() {
      if (submitting) return;
      submitting = true;

      showLoading();

      if (PREVIEW) {
        setTimeout(function () {
          submitting = false;
          showDone(null);
        }, 900);
        return;
      }

      var body = new FormData();
      body.append('payload', JSON.stringify(payload(false)));
      files.forEach(function (entry, i) {
        body.append('photo_' + i, entry.file, entry.file.name);
      });

      fetch(SUBMIT_URL, { method: 'POST', mode: 'cors', body: body })
        .then(function (res) {
          return res.json().catch(function () {
            return { ok: res.ok };
          });
        })
        .then(function (res) {
          submitting = false;
          if (res && res.ok) {
            clearSaved();
            showDone(res.id || null);
          } else {
            showFailure(res && res.error);
          }
        })
        .catch(function () {
          submitting = false;
          showFailure(null);
        });
    }

    function showLoading() {
      if (progressFill) progressFill.style.width = '100%';
      var screen = node(
        '<div class="dvw-st__screen dvw-st__screen--center">' +
          '<div class="dvw-st__spinner"></div>' +
          '<h2 class="dvw-st__title">Sending your details</h2>' +
          '<p class="dvw-st__desc"></p>' +
          '</div>'
      );
      screen.querySelector('.dvw-st__desc').textContent = files.length
        ? 'Uploading your photos securely.'
        : 'One moment.';
      swap(screen);
      scrollIntoPlace();
    }

    function showDone(reference) {
      var c = answers.contact || {};

      var screen = node(
        '<div class="dvw-st__screen dvw-st__screen--center">' +
          '<div class="dvw-st__tick-badge">' +
          ICON_CHECK +
          '</div>' +
          '<h1 class="dvw-st__title dvw-st__title--lg"></h1>' +
          '<p class="dvw-st__desc"></p>' +
          '<div class="dvw-st__actions"></div>' +
          '</div>'
      );

      screen.querySelector('.dvw-st__title').textContent = c.first ? 'Thank you, ' + c.first : 'Thank you';
      screen.querySelector('.dvw-st__desc').textContent =
        "We've received your watch details. A specialist will review them and reply shortly by email or text.";

      var actions = screen.querySelector('.dvw-st__actions');
      var browse = document.createElement('a');
      browse.className = 'dvw-st__btn';
      browse.href = SHOP_ALL;
      browse.textContent = 'Browse the collection';
      actions.appendChild(browse);

      var home = document.createElement('a');
      home.className = 'dvw-st__btn dvw-st__btn--ghost';
      home.href = HOME;
      home.textContent = 'Back to home';
      actions.appendChild(home);

      if (reference) {
        var ref = node('<p class="dvw-st__ref"></p>');
        ref.textContent = 'Reference ' + reference;
        screen.appendChild(ref);
      }

      swap(screen);
      scrollIntoPlace();
    }

    function showFailure(message) {
      var screen = node(
        '<div class="dvw-st__screen dvw-st__screen--center">' +
          '<h2 class="dvw-st__title">That didn\'t go through</h2>' +
          '<p class="dvw-st__desc"></p>' +
          '<div class="dvw-st__actions"></div>' +
          '</div>'
      );
      screen.querySelector('.dvw-st__desc').textContent =
        message ||
        'Check your connection and try again. Your answers are still here' +
          (PHONE ? ', or text us at ' + PHONE + '.' : '.');

      var retry = node('<button type="button" class="dvw-st__btn">Try again</button>');
      retry.addEventListener('click', function () {
        submit();
      });
      screen.querySelector('.dvw-st__actions').appendChild(retry);

      var back = node(
        '<button type="button" class="dvw-st__btn dvw-st__btn--ghost">Back to my details</button>'
      );
      back.addEventListener('click', function () {
        order = visibleQuestions();
        pos = order.length - 1;
        render();
      });
      screen.querySelector('.dvw-st__actions').appendChild(back);

      swap(screen);
      scrollIntoPlace();
    }

    /* -------------------------------------------------------------- keyboard

       Scoped to this section: the listener bails out when focus sits anywhere else on the page,
       so the theme's header search and cart drawer keep their own keys. */

    document.addEventListener('keydown', function (event) {
      var target = event.target;
      if (target !== document.body && !root.contains(target)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      var screen = activeScreen();
      if (!screen || !screen.querySelector('[data-next]')) return;

      var qi = order[pos];
      if (qi === undefined) return;
      var q = QUESTIONS[qi];
      var tag = (target.tagName || '').toLowerCase();
      var typing = tag === 'input' || tag === 'textarea' || tag === 'select';

      if (q.type === 'choice' && !typing) {
        var idx = KEY_LETTERS.indexOf((event.key || '').toUpperCase());
        if (idx > -1 && idx < q.options.length) {
          event.preventDefault();
          var btn = screen.querySelectorAll('.dvw-st__choice')[idx];
          if (btn) btn.click();
          return;
        }
      }

      if (event.key !== 'Enter') return;
      if (tag === 'textarea') return;
      if (tag === 'button' || tag === 'a') return;
      if (target === document.body && q.type !== 'intro') return;

      event.preventDefault();
      if (q.type === 'intro') goTo(pos + 1);
      else if (q.type === 'review') submit();
      else advance(q, screen);
    });

    window.addEventListener('popstate', function (event) {
      var state = event.state;
      var next = state && typeof state.dvwPos === 'number' ? state.dvwPos : 0;
      order = visibleQuestions();
      pos = Math.max(0, Math.min(next, order.length - 1));
      render();
    });

    /* -------------------------------------------------------------- init */

    var noscript = root.querySelector('noscript');
    if (noscript) noscript.remove();

    fitToScreen();
    /* Re-measured on load because webfonts can still change the header's height after
       DOMContentLoaded, and on resize for rotation and window changes. */
    window.addEventListener('load', fitToScreen);
    window.addEventListener('resize', fitToScreen);

    var restored = restore();
    applyDeepLink();
    /* The backend keys its routing off form_type, and this page only ever submits one kind. */
    answers.form_type = SELL;

    order = visibleQuestions();
    pos = restored ? firstIncompletePos() : 0;

    try {
      history.replaceState({ dvwPos: pos }, '');
    } catch (err) {
      /* ignore */
    }
    historyReady = true;

    render();
  }

  function init() {
    document.querySelectorAll('[data-dvw-sell-trade]').forEach(boot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor re-renders the section on every setting change. */
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-dvw-sell-trade]');
    if (root) boot(root);
  });
})();
